import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { createSelector } from '@reduxjs/toolkit';
import { useAppSelector } from 'hooks/redux';
import { selectChannels } from 'store/channels/channelsSlice';
import { selectPatient } from 'store/patients/patientsSlice';

import { PhoneCallContextProps, PhoneCallPatient } from './phoneCallContext.types';

const defaultPhoneCallContextValues: PhoneCallContextProps = {
  call: null,
  device: null,
  isCallButtonDisabled: false,
  isPhoneCallInProgress: false,
  phoneCallPatient: null,
  position: 'default',
  resetPhoneCallProps: () => {},
  setCall: () => {},
  setDevice: () => {},
  setIsPhoneCallInProgress: () => {},
  setPhoneCallPatient: () => {},
  setShowCallCard: () => {},
  setToken: () => {},
  setupPhoneCall: () => {},
  showCallCard: false,
  token: null
};

const PhoneCallContext = createContext<PhoneCallContextProps>(defaultPhoneCallContextValues);

const selectChatState = createSelector([selectPatient, selectChannels], (patient, channels) => ({
  currentChannel: channels.currentChannel,
  patient: patient.patientInfo
}));

const PhoneCallProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { currentChannel, patient } = useAppSelector(selectChatState);

  const [phoneCallPatient, setPhoneCallPatient] = useState(
    defaultPhoneCallContextValues.phoneCallPatient
  );
  const [position, setPosition] = useState(defaultPhoneCallContextValues.position);
  const [token, setToken] = useState(defaultPhoneCallContextValues.token);
  const [showCallCard, setShowCallCard] = useState(defaultPhoneCallContextValues.showCallCard);
  const [isCallButtonDisabled, setIsCallButtonDisabled] = useState(
    defaultPhoneCallContextValues.isCallButtonDisabled
  );
  const [device, setDevice] = useState(defaultPhoneCallContextValues.device);
  const [call, setCall] = useState(defaultPhoneCallContextValues.call);
  const [isPhoneCallInProgress, setIsPhoneCallInProgress] = useState(
    defaultPhoneCallContextValues.isPhoneCallInProgress
  );

  const callRef = useRef(call);

  useEffect(() => {
    callRef.current = call;
  }, [call]);

  const setupPhoneCall = (
    patientInfo: PhoneCallPatient,
    position: 'default' | 'center' = 'default'
  ) => {
    if (!patientInfo) return;

    setPosition(position);
    setPhoneCallPatient(patientInfo);
    setShowCallCard(true);
    setIsPhoneCallInProgress(false);
    setIsCallButtonDisabled(true);
  };

  const resetPhoneCallProps = () => {
    // NOTE: to be sure if the call active, it will be disconnected
    if (callRef.current) {
      callRef.current.disconnect();
    }

    if (device) {
      device.destroy();
    }

    setShowCallCard(false);
    setToken(null);
    setPhoneCallPatient(null);
    setDevice(null);
    setIsPhoneCallInProgress(false);
    setIsCallButtonDisabled(false);
  };

  useEffect(() => {
    // This effect is used to disable the call button

    if (currentChannel && currentChannel?.patientId !== patient?._id) {
      // If the current channel patient is not the same as the one in the context
      setIsCallButtonDisabled(true);
    } else if (showCallCard || isPhoneCallInProgress) {
      // If the call card is shown or call is in progress, always keep the button disabled
      setIsCallButtonDisabled(true);
    } else {
      setIsCallButtonDisabled(false);
    }
  }, [showCallCard, isPhoneCallInProgress, currentChannel, patient?._id]);

  useEffect(() => {
    // this effect is used to reset the call state

    // 1. There is no patient in the store on the prospects table, but the call card
    // should be shown with the prospect patient which is the phoneCallPatient
    // 2. If position is default, it means that the call card is shown on the slidingPane
    if (
      showCallCard &&
      !(position === 'default' ? patient : phoneCallPatient)?._id &&
      !isPhoneCallInProgress
    ) {
      // If the call card is shown and the patient is not set, reset the call state
      // for example, when we where on a patient page or on task modal, opened the call card
      // and then we close the task modal or switch the page
      resetPhoneCallProps();
    }
  }, [showCallCard, isPhoneCallInProgress, patient?._id, phoneCallPatient?._id]);

  const values = useMemo(
    () => ({
      call,
      device,
      isCallButtonDisabled,
      isPhoneCallInProgress,
      phoneCallPatient,
      position,
      resetPhoneCallProps,
      setCall,
      setDevice,
      setIsPhoneCallInProgress,
      setPhoneCallPatient,
      setShowCallCard,
      setToken,
      setupPhoneCall,
      showCallCard,
      token
    }),
    [
      showCallCard,
      isPhoneCallInProgress,
      token,
      phoneCallPatient,
      device,
      call,
      isCallButtonDisabled,
      setupPhoneCall,
      isPhoneCallInProgress,
      setIsPhoneCallInProgress,
      position
    ]
  );

  return <PhoneCallContext.Provider value={values}>{children}</PhoneCallContext.Provider>;
};

const usePhoneCall = () => {
  const context = useContext(PhoneCallContext);
  if (context === undefined) {
    throw new Error('usePhoneCall must be used within PhoneCallProvider');
  }
  return context;
};

export { PhoneCallProvider, usePhoneCall };
